import React from "react";
import { Typography } from "antd";
import { SearchOutlined, EyeOutlined, MessageOutlined, CalendarOutlined, StarOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Title, Text } = Typography;

const Section = styled.section`
  background: #f0f2f5;
  padding: 96px 20px 64px 20px;
`;

const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const BentoCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    border-color: #6841eb;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;

const IconContainer = styled.div`
  background: linear-gradient(135deg, #6841eb 0%, #764ba2 100%);
  border-radius: 12px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled(Title)`
  font-size: 1.5rem !important;
  font-weight: 700 !important;
  margin: 0 0 8px 0 !important;
  line-height: 1.3 !important;
  color: #1a1a1a !important;
`;

const FeatureDescription = styled(Text)`
  font-size: 0.9rem !important;
  line-height: 1.5 !important;
  color: #666 !important;
  font-weight: 400 !important;
  display: block;
`;

const ImageContainer = styled.div`
  margin-top: 28px;
  border-radius: 12px;
  overflow: hidden;
  background: #f8f9fa;
  border: 1px solid rgba(0, 0, 0, 0.08);
  height: 260px;
  flex: 1;
`;

const FeatureImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const features = [
  {
    icon: <StarOutlined />,
    title: "One-Click Applications",
    description: "Stop filling out the same form over and over. Apply to multiple jobs with one profile and spend your energy on the right opportunities, not paperwork.",
    image: "/oneclick.png",
    alt: "One-Click Applications",
    size: "large"
  },
  {
    icon: <EyeOutlined />,
    title: "Live Application Tracking",
    description: "No more waiting and wondering. Know exactly where you stand at every stage so you can plan your next move with confidence.",
    image: "/jobtrack.png",
    alt: "Live Application Tracking",
    size: "medium"
  },

  {
    icon: <MessageOutlined />,
    title: "Actionable Feedback",
    description: "Most platforms ghost you on rejection. ChronoFlow tells you why, so every application makes you a stronger candidate for the next one.",
    image: "/feedbackcandidate.png",
    alt: "Actionable Feedback",
    size: "tall"
  },
  {
    icon: <CalendarOutlined />,
    title: "Effortless Scheduling",
    description: "No back and forth emails trying to find a time. Set your availability once and interviews get scheduled around you automatically.",
    image: "/availability.png",
    alt: "Effortless Scheduling",
    size: "large-square"
  }
];

const JobSeekerFeatures = () => (
  <Section>
    <div style={{ maxWidth: 1300, margin: "0 auto" }}>
      <Title level={2} style={{ color: "#6841eb", textAlign: "center", marginBottom: 64, fontWeight: 800, fontSize: "2.8rem", letterSpacing: "-1px" }}>
        Land Your Dream Job Faster
      </Title>
      <BentoGrid>
        {features.map((feature, idx) => (
          <BentoCard key={feature.title} className={feature.size}>
            <div>
              <CardHeader>
                <IconContainer>{feature.icon}</IconContainer>
                <CardContent>
                  <FeatureTitle level={3}>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </CardContent>
              </CardHeader>
            </div>
            <ImageContainer>
              <FeatureImage src={feature.image} alt={feature.alt} />
            </ImageContainer>
          </BentoCard>
        ))}
      </BentoGrid>
    </div>
  </Section>
);

export default JobSeekerFeatures;